import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERIES_KEYS } from "queries";
import { CategoryApi, categoriesApi } from "./categoriesApi";

export const useCategories = () => {
  const queryClient = useQueryClient();
  const { isLoading, isFetching, data } = useQuery({
    queryKey: QUERIES_KEYS.CATEGORIES,
    queryFn: () => categoriesApi.fetchCategories(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.CATEGORIES,
    });
  };

  return { invalidate, isLoading, isFetching, categories: data?.data.data ?? [], total: data?.data.total ?? 0 };
};

const buildHierarchicalLabel = (cat: CategoryApi, allCats: CategoryApi[]): string => {
  if (!cat.parentId) return cat.name;
  const parent = allCats.find((c) => c.id === cat.parentId);
  return parent ? `${buildHierarchicalLabel(parent, allCats)} > ${cat.name}` : cat.name;
};

/** Drops `rootId` and everything below it: none of them can become its parent without a cycle. */
export const excludeSubtree = (categories: CategoryApi[], rootId: number): CategoryApi[] => {
  const removed = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of categories) {
      if (c.parentId != null && removed.has(c.parentId) && !removed.has(c.id)) {
        removed.add(c.id);
        grew = true;
      }
    }
  }
  return categories.filter((c) => !removed.has(c.id));
};

/** @param excludeSubtreeOf when picking a parent for this category, leave out the category and its descendants */
export const useCategoriesOptions = (excludeSubtreeOf?: number) => {
  const { isLoading, categories } = useCategories();

  const filtered = excludeSubtreeOf ? excludeSubtree(categories, excludeSubtreeOf) : categories;

  const options = filtered.map((x) => ({
    value: x.id,
    label: buildHierarchicalLabel(x, categories),
  }));

  return { loading: isLoading, options };
};
